/**
 * What a relationship row actually points at -- and whether it has moved.
 *
 * AN EDGE STORES LOGICAL OBJECTS, NOT REVISIONS, so by default it resolves to whichever revision
 * each end is on TODAY. That is the right default for "see also", and wrong for the statements a
 * quality system makes: "this deviation was raised against SOP-00018 REV 2" is about REV 2 and stays
 * about REV 2 after REV 3 is released. So the child end may carry a PIN.
 *
 * THREE STATES, AND THE READER HAS TO BE ABLE TO TELL THEM APART:
 *
 *   floating, still where it started  -- the ordinary case, and it says nothing extra
 *   floating, MOVED since it was made -- resolves to REV 3, was made against REV 1
 *   pinned                            -- resolves to REV 2 and will not follow the record forward
 *
 * The middle one is the case that has no voice without the drift baseline, and it is the one worth
 * surfacing: nobody decided the link should now mean REV 3, it just does.
 *
 * ONLY THE CHILD END CAN BE PINNED. An edge is authored from the parent toward the child -- you are
 * on X and you add related object Y -- so the target is the end whose revision the author chose.
 * Pinning the source would mean references belong to a revision and are copied forward on revise
 * (Enlil's model); that is a change to the revise machinery, not to this file.
 */

export type EndpointResolution = {
    /** The name to show, from whichever revision the edge actually resolves to. */
    name: string | null;
    /** "SOP-00018", off the logical object -- never revision-specific. */
    objectId: string | null;
    /** The revision being shown: the pin when pinned, otherwise the current one. */
    displayRevision: string | null;
    /** Where a click should go. Keyed on a REVISION, which is what the detail route takes. */
    revisionUSID: string | null;
    /** The status OF THE REVISION BEING SHOWN, so a pinned edge reports the pinned revision's state. */
    status: string | null;
    classification: string | null;
    pinned: boolean;
    /** Pinned to a revision that no longer exists -- shown as floating, but say so. */
    pinBroken: boolean;
    /** The edge does not resolve to the revision it was made against. */
    drifted: boolean;
    /** One sentence for a tooltip, or null when there is nothing worth saying. */
    note: string | null;
};

type Row = Record<string, any> & {get?: (field: string) => any};

const read = (row: Row | undefined, field: string) =>
    (row?.get ? row.get(field) : row?.[field]) ?? null;

const revLabel = (rev: any) => (rev === null || rev === undefined || rev === "" ? null : `REV ${rev}`);

/**
 * Resolve one end of an edge.
 *
 * `end` is "Child" or "Parent". The parent end has no pin and no baseline of its own to compare
 * against, so it always resolves to the current revision -- passing it here rather than special
 * casing at the call site keeps one shape for both columns.
 */
export function resolveEndpoint(row: Row | undefined, end: "Child" | "Parent"): EndpointResolution {
    const currentName = read(row, `${end}Name`);
    const currentRevisionUSID = read(row, `${end}RevisionUSID`);
    const currentDisplayRevision = read(row, `${end}DisplayRevision`);
    const currentStatus = read(row, `${end}Status`);

    const base: EndpointResolution = {
        name: currentName,
        objectId: read(row, `${end}ObjectID`),
        displayRevision: currentDisplayRevision,
        revisionUSID: currentRevisionUSID,
        status: currentStatus,
        classification: read(row, `${end}ObjectClassification`),
        pinned: false,
        pinBroken: false,
        drifted: false,
        note: null,
    };

    if (end === "Parent") return base;

    const pinnedUSID = read(row, "ChildPinnedRevisionUSID");

    if (pinnedUSID) {
        const pinnedRevision = read(row, "ChildPinnedDisplayRevision");

        // THE PIN NAMES A REVISION THAT IS GONE. There is deliberately no foreign key -- a revision
        // can be hard-deleted by tenant cleanup and an FK would take the edge with it -- so the join
        // comes back empty and every pinned field is null. Falling back to the current revision keeps
        // the row usable; saying so keeps it honest, because silently showing the current revision is
        // exactly the behaviour the pin was chosen to prevent.
        if (pinnedRevision === null && read(row, "ChildPinnedName") === null) {
            return {...base, pinBroken: true,
                    note: "Pinned to a revision that no longer exists. Showing the current revision instead."};
        }

        const movedOn = currentDisplayRevision !== null && pinnedRevision !== currentDisplayRevision;
        return {
            ...base,
            name: read(row, "ChildPinnedName") ?? currentName,
            displayRevision: pinnedRevision,
            revisionUSID: pinnedUSID,
            status: read(row, "ChildPinnedStatus") ?? currentStatus,
            pinned: true,
            drifted: movedOn,
            note: movedOn
                ? `Pinned to ${revLabel(pinnedRevision)}. The record has since moved to ${revLabel(currentDisplayRevision)}.`
                : `Pinned to ${revLabel(pinnedRevision)}, which is the current revision.`,
        };
    }

    // FLOATING. Compare against the baseline stamped when the edge was made. Both halves are
    // required: edges created before the baseline column existed have none, and "unknown" must not
    // be reported as "has not moved" -- that would be inventing history rather than declining to
    // state it.
    const baselineUSID = read(row, "ChildRevisionAtCreationUSID");
    const baselineRevision = read(row, "ChildRevisionAtCreationDisplayRevision");

    if (baselineUSID && currentRevisionUSID && String(baselineUSID) !== String(currentRevisionUSID)) {
        return {
            ...base,
            drifted: true,
            note: baselineRevision
                ? `Follows the current revision. Made against ${revLabel(baselineRevision)}, now showing ${revLabel(currentDisplayRevision)}.`
                : `Follows the current revision, and has moved since it was made.`,
        };
    }

    return base;
}

/** "SOP-00018 REV 2", or just the id when there is no revision to name. */
export function endpointLabel(resolution: EndpointResolution): string {
    if (!resolution.objectId) return "";
    const rev = revLabel(resolution.displayRevision);
    return rev ? `${resolution.objectId} ${rev}` : resolution.objectId;
}
